import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { type Device } from "../Device";

import { invalidateDeviceListings } from "./invalidateDeviceListings";

interface SetDevicesTypeVariables {
  /** Each device is updated in its own org, read from its `org_id`. */
  devices: readonly Device[];
  /** The `dt_` ID to give every device, or `null` to clear their types. */
  deviceTypeId: string | null;
}

/**
 * Gives several devices one type, with one request each, since the service
 * has no bulk update. One failure does not stop the others: the result holds
 * each device's outcome, in the order of `devices`, and the mutation itself
 * never fails, so callers read failures from the result rather than `onError`.
 */
export function useSetDevicesType() {
  const { devices: deviceService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<
    PromiseSettledResult<Device>[],
    Error,
    SetDevicesTypeVariables
  >({
    mutationFn: ({ devices, deviceTypeId }) =>
      Promise.allSettled(
        devices.map((device) =>
          deviceService.updateDevice(
            device.device_id,
            { device_type_id: deviceTypeId },
            { resourceOwnerId: device.org_id },
          ),
        ),
      ),
    onSuccess: (results, { devices }) => {
      invalidateDeviceListings(queryClient, devices);

      // Each response is the device's newest state, so seed its detail cache
      // rather than making the device page refetch.
      for (const result of results) {
        if (result.status === "fulfilled") {
          queryClient.setQueryData(
            ["device", result.value.device_id],
            result.value,
          );
        }
      }
    },
  });
}
